import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useDeleteAwayScheduleBlock } from '@/hooks/useAwaySchedule';
import { useToaster } from '@/hooks/useToaster';
import type { AwayScheduleBlock } from '@/services/getAwayScheduleV2';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { setUserStatusV2 } from '@/services/setUserStatusV2';
import { trackAwayPeriodRemoved } from '@/util/analyticsEvents';
import { ApiError } from '@/util/fetchClient';

export const WELCOME_BACK = 'Welcome back. You’re accepting inquiries again.';

type EndAwayInput = {
  /** The block covering today, when there is one. */
  block?: AwayScheduleBlock;
  /** Away through the old status switch, which has no end date. */
  legacyAway: boolean;
  today: string;
};

/**
 * Ends the time away that covers today in one tap, from outside the Availability sheet. It
 * neither asks first nor offers Undo: setting time away again from Account is the way back.
 */
export function useEndAwayNow(userId?: string) {
  const queryClient = useQueryClient();

  const remove = useDeleteAwayScheduleBlock();

  const { showSuccess, showError } = useToaster();

  const [isEnding, setIsEnding] = useState(false);

  /** A ref, not state, so two taps before a re-render cannot both send a request. */
  const inFlight = useRef(false);

  const refreshUser = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getCurrentUserV2QueryOptions(userId).queryKey,
      }),
    [queryClient, userId]
  );

  const endAwayNow = useCallback(
    async (input: EndAwayInput) => {
      if (inFlight.current || (!input.block && !input.legacyAway)) {
        return;
      }

      inFlight.current = true;

      setIsEnding(true);

      try {
        if (input.legacyAway) {
          await setUserStatusV2({ status: '1' });

          await refreshUser();
        }

        if (input.block) {
          try {
            await remove.mutateAsync(input.block.id);
          } catch (error) {
            // Already gone is what was asked for, so it still counts as ended.
            if (!(error instanceof ApiError && error.status === 404)) {
              throw error;
            }
          }
        }

        trackAwayPeriodRemoved({ kind: 'ended_early' });

        showSuccess({ title: WELCOME_BACK, liveRegion: 'polite' });
      } catch {
        showError({ title: 'Couldn’t end your time away. Try again.' });
      } finally {
        setIsEnding(false);

        inFlight.current = false;
      }
    },
    [refreshUser, remove, showError, showSuccess]
  );

  return { endAwayNow, isEnding };
}
